import { Button, Center, Heading, Icon, Text, VStack } from '@chakra-ui/react'
import { ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { useUser } from '../../contexts/UserContext'

/**
 * O check verde da tela de sucesso.
 *
 * Era o mesmo `path` colado em três arquivos (`EntregaDeTrabalhoSubmitPage`,
 * `StudyCreditSubmitPage`, `SalesReceiptSubmitPage`); as seis `*CompletePage` não tinham ícone
 * nenhum, e passam a ter este.
 */
const CheckIcon = () => (
  <Icon viewBox="0 0 24 24" boxSize="16" color="green.400" mb="4" aria-hidden>
    <path
      fill="currentColor"
      d="M12 2a10 10 0 100 20 10 10 0 000-20zm-1.2 14.2L6.6 12l1.4-1.4 2.8 2.8 5.6-5.6L17.8 9l-7 7.2z"
    />
  </Icon>
)

export interface IFormSuccessAction {
  label: string
  /** Rota interna. Um botão sem `to` é um `onClick` (o "Enviar outro trabalho", que só limpa o formulário). */
  to?: string
  onClick?: () => void
  /**
   * Só aparece para quem está logado, lido do `useUser()`.
   *
   * É o "Retornar para página inicial" das telas públicas: quem chega pelo link do formulário
   * não tem para onde voltar. As cinco páginas liam o `localStorage` na mão, num `useEffect`
   * com estado próprio, com o `useUser()` bem ali do lado.
   */
  loggedOnly?: boolean
  /**
   * `outline` para a ação secundária — o "Enviar outro trabalho", que reabre o formulário em vez
   * de levar para outro lugar. `solid` (padrão) é a saída principal.
   */
  variant?: 'solid' | 'outline'
}

export interface IFormSuccessProps {
  /** A frase principal. Sai como o `h2` da tela — o `h1` é o do `PublicFormLayout`. */
  title: ReactNode
  /** O parágrafo que diz o que acontece agora. */
  description?: ReactNode
  /** Linhas de fecho — o princípio sagrado, o `#ABRACEVAI`. Uma por parágrafo, menores. */
  notes?: ReactNode[]
  /** Ícone do topo. `null` tira, para a tela que não quer o check. */
  icon?: ReactNode
  /** Os caminhos de saída, empilhados sob o texto. */
  actions?: IFormSuccessAction[]
  /** Conteúdo entre o texto e os botões — o placar da Avaliação de Experiência. */
  children?: ReactNode
}

/**
 * A tela de "enviado com sucesso" dos formulários públicos.
 *
 * Eram nove telas em duas famílias. As seis `*CompletePage` marcavam o corpo do texto como
 * `<Heading as="h3">`/`as="h4">` — parágrafo com marcação de título — e como o
 * `PublicFormLayout` já emite o `h1`, o outline dessas páginas era `h1 → h2 → h3 → h4 h4` sem
 * nenhum conteúdo sob eles. `AceleraMagentaCompletePage` e `ConexaoMagentaCompletePage` eram
 * idênticas exceto pelo nome do programa. As três telas de sucesso inline dos formulários de
 * envio repetiam o mesmo `<Center><Box textAlign="center" maxW="md">` e o mesmo `path` de check.
 *
 * Havia ainda duas receitas de botão dentro da mesma família: `<br />` como espaçador em três
 * páginas, e um bloco de oito props copiado em duas outras. Aqui a saída é declarada
 * (`actions`), e a geometria — largura, quebra de linha, altura automática, espaçamento — é do
 * componente. `RefundsCompletePage`, que não tinha botão nenhum e era um beco sem saída,
 * continua sem: a decisão de ter ou não saída é da tela, o desenho dela não.
 */
export default function FormSuccess({ title, description, notes, icon, actions, children }: IFormSuccessProps) {
  const { id } = useUser()
  const isLogged = id !== null
  const visibleActions = (actions ?? []).filter((action) => !action.loggedOnly || isLogged)

  return (
    <Center flexDirection="column" textAlign="center">
      {icon === undefined ? <CheckIcon /> : icon}

      <Heading as="h2" mb={description || notes?.length ? 3 : 0}>
        {title}
      </Heading>

      {description && (
        <Text color="gray.600" maxW="md">
          {description}
        </Text>
      )}

      {notes?.map((note, index) => (
        <Text variant="caption" key={index} maxW="md" mt={2}>
          {note}
        </Text>
      ))}

      {children}

      {visibleActions.length > 0 && (
        <VStack spacing={3} mt={6} w={{ base: '100%', md: 'auto' }}>
          {visibleActions.map((action) => (
            <Button
              key={action.label}
              colorScheme="magenta"
              variant={action.variant}
              // O rótulo é uma frase inteira ("Realizar uma nova Solicitação de Movimentação de
              // Pessoal"): sem a quebra e a altura automática ele estoura a largura no celular.
              w={{ base: '100%', md: 'auto' }}
              whiteSpace="normal"
              height="auto"
              py={3}
              {...(action.to ? { as: Link, to: action.to, _hover: { color: 'white', textDecoration: 'none' } } : {})}
              onClick={action.onClick}
            >
              {action.label}
            </Button>
          ))}
        </VStack>
      )}
    </Center>
  )
}
